/**
 * GPS-based ETA calculation utilities
 * Calculates distance and estimated time of arrival based on truck GPS coordinates
 */

/**
 * Calculate distance between two GPS coordinates using Haversine formula
 * Returns distance in miles
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return Math.round(distance);
}

function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Calculate ETA based on distance and average speed
 * Returns hours until arrival
 */
export function calculateETA(distanceMiles: number, avgSpeedMph: number = 55): number {
  return distanceMiles / avgSpeedMph;
}

/**
 * Format ETA for display
 */
export function formatETA(hours: number): string {
  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes} min${minutes !== 1 ? 's' : ''}`;
  } else if (hours < 24) {
    const roundedHours = Math.round(hours);
    return `${roundedHours} hour${roundedHours !== 1 ? 's' : ''}`;
  } else {
    const days = Math.round(hours / 24);
    return `${days} day${days !== 1 ? 's' : ''}`;
  }
}

/**
 * Geocode a city, state to approximate lat/lng
 * This is a simple approximation - for production use a real geocoding API
 */
export function approximateGeocode(city: string, state: string): { lat: number; lng: number } | null {
  // Major city coordinates database (simplified)
  const cityCoords: Record<string, { lat: number; lng: number }> = {
    // Format: "CITY, STATE": coordinates
    "HICKORY, NC": { lat: 35.7344, lng: -81.3412 },
    "HUBER HEIGHTS, OH": { lat: 39.8439, lng: -84.1246 },
    "BROWNSVILLE, TN": { lat: 35.5934, lng: -89.2625 },
    "CULLMAN, AL": { lat: 34.1748, lng: -86.8436 },
    "PORTAGE, MI": { lat: 42.2011, lng: -85.5800 },
    "PAINESVILLE, OH": { lat: 41.7245, lng: -81.2457 },
    "SAINT JAMES, MO": { lat: 37.9975, lng: -91.6157 },
    "SALISBURY, NC": { lat: 35.6709, lng: -80.4742 },
    "NORTH KANSAS CITY, MO": { lat: 39.1328, lng: -94.5625 },
    "PANAMA CITY BEACH, FL": { lat: 30.1766, lng: -85.8055 },
    "MINDEN, NE": { lat: 40.4986, lng: -98.9476 },
    "KNOXVILLE, TN": { lat: 35.9606, lng: -83.9207 },
    "RONKONKOMA, NY": { lat: 40.8151, lng: -73.1123 },
    "MEMPHIS, TN": { lat: 35.1495, lng: -90.0490 },
    "KISSIMMEE, FL": { lat: 28.2920, lng: -81.4076 },
    "NEWPORT NEWS, VA": { lat: 37.0871, lng: -76.4730 },
    "RINCON, GA": { lat: 32.2938, lng: -81.2329 },
    "TITUSVILLE, PA": { lat: 41.6267, lng: -79.6736 },
    "WALKER, LA": { lat: 30.4877, lng: -90.8618 },
    "PARIS, KY": { lat: 38.2098, lng: -84.2530 },
    "NEWTON, NC": { lat: 35.6699, lng: -81.2220 },
    "ZOLFO SPRINGS, FL": { lat: 27.4931, lng: -81.7959 },
    "MARQUETTE, MI": { lat: 46.5436, lng: -87.3954 },
  };

  const key = `${city.toUpperCase()}, ${state.toUpperCase()}`;
  return cityCoords[key] || null;
}
